import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import OnboardingStepper from './OnboardingStepper';
import FileUploadField from './FileUploadField';
import { authService } from '../../services/apiService';
import { useAuth, USER_ROLES } from '../../auth/AuthContext';
import { getProfileDisplayEmail } from '../../utils/displayName';
import { ONBOARDING_THEME } from './onboardingTheme';

const STEPS = ['Personal Profile', 'Address', 'Vehicle Details'];
const DRAFT_KEY = 'onboarding_customer_draft';

const Wrap = styled.div`
  height: 100vh; overflow: hidden; background: ${ONBOARDING_THEME.pageBg};
  padding: 16px 20px; display: flex; align-items: center; justify-content: center;
`;
const Card = styled.div`
  max-width: 960px; width: 100%; max-height: calc(100vh - 32px);
  background: ${ONBOARDING_THEME.contentBg}; border-radius: ${ONBOARDING_THEME.radius};
  padding: 20px 24px; box-shadow: ${ONBOARDING_THEME.shadowCard};
  display: flex; flex-direction: column; overflow: hidden;
`;
const StepContent = styled.div` flex: 1; min-height: 0; overflow-y: auto; `;
const Title = styled.h1` font-size: 1.5rem; color: ${ONBOARDING_THEME.text}; margin: 0 0 6px 0; font-weight: 700; `;
const Sub = styled.p` color: ${ONBOARDING_THEME.textMuted}; font-size: 14px; margin: 0 0 20px 0; `;
const Row = styled.div` display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 12px 18px; margin-bottom: 12px; `;
const Row2 = styled.div` display: grid; grid-template-columns: 1fr 1fr; gap: 12px 18px; margin-bottom: 12px; `;
const Row3 = styled.div` display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px 18px; margin-bottom: 12px; `;
const Field = styled.div` min-width: 0; `;
const Label = styled.label` display: block; font-size: 12px; font-weight: 600; color: ${ONBOARDING_THEME.text}; margin-bottom: 6px; `;
const Input = styled.input`
  width: 100%; padding: 12px 14px; border: 1px solid ${ONBOARDING_THEME.border}; border-radius: ${ONBOARDING_THEME.radiusSm};
  font-size: 14px; box-sizing: border-box; background: ${ONBOARDING_THEME.contentBg};
  &:focus { outline: none; border-color: ${ONBOARDING_THEME.accent}; box-shadow: 0 0 0 2px ${ONBOARDING_THEME.accentLight}; }
`;
const Select = styled.select`
  width: 100%; padding: 12px 14px; border: 1px solid ${ONBOARDING_THEME.border}; border-radius: ${ONBOARDING_THEME.radiusSm};
  font-size: 14px; box-sizing: border-box; background: ${ONBOARDING_THEME.contentBg};
  &:focus { outline: none; border-color: ${ONBOARDING_THEME.accent}; box-shadow: 0 0 0 2px ${ONBOARDING_THEME.accentLight}; }
`;
const BtnRow = styled.div` display: flex; justify-content: space-between; margin-top: 20px; gap: 12px; flex-shrink: 0; `;
const Btn = styled.button`
  padding: 12px 20px; border-radius: ${ONBOARDING_THEME.radiusSm}; font-weight: 600; cursor: pointer; font-size: 13px; border: none;
  &.primary { background: ${ONBOARDING_THEME.accent}; color: white; }
  &.primary:hover { background: ${ONBOARDING_THEME.accentDark}; }
  &.secondary { background: ${ONBOARDING_THEME.contentBg}; color: ${ONBOARDING_THEME.text}; border: 1px solid ${ONBOARDING_THEME.border}; }
  &.secondary:hover { background: ${ONBOARDING_THEME.inputBg}; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;
const ErrorP = styled.p` color: ${ONBOARDING_THEME.error}; font-size: 13px; margin: 0 0 10px 0; font-weight: 500; `;
const Section = styled.div`
  border-radius: ${ONBOARDING_THEME.radiusSm}; padding: 18px 20px; margin-bottom: 14px;
  background: ${ONBOARDING_THEME.contentBg}; border: 1px solid ${ONBOARDING_THEME.borderLight};
  box-shadow: ${ONBOARDING_THEME.shadow};
`;

const defaultPersonal = {
  firstName: '', lastName: '', gender: '', dob: '', profilePhoto: '', preferredLanguage: 'en',
  email: '', phone: '', phoneCode: '+91'
};
const defaultAddress = () => ({
  area: '', addressLine2: '', landmark: '', city: '', state: '', country: 'India', pincode: '',
  lat: '', lng: '', label: 'Home', isDefault: false
});
const defaultVehicle = () => ({
  vehicleType: '', brand: '', model: '', variant: '', fuelType: '', year: '', registrationNumber: '', odometer: '', rcUpload: ''
});
export default function OnboardingCustomer() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, getDashboardPath, updateUser } = useAuth();
  const role = location.state?.role || user?.role || USER_ROLES.CUSTOMER;

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [personal, setPersonal] = useState(defaultPersonal);
  const [addresses, setAddresses] = useState([defaultAddress()]);
  const [vehicles, setVehicles] = useState([defaultVehicle()]);

  const loadDraft = useCallback(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        if (d.personal) setPersonal(d.personal);
        if (d.addresses?.length) setAddresses(d.addresses);
        if (d.vehicles?.length) setVehicles(d.vehicles);
        if (d.step) setStep(Math.min(Number(d.step), STEPS.length));
      }
    } catch (_) {}
  }, []);

  useEffect(() => { loadDraft(); }, [loadDraft]);

  useEffect(() => {
    if (!user) return;
    const displayEmail = getProfileDisplayEmail(user);
    const hasPhone = !!(user.phone && String(user.phone).trim());
    setPersonal((prev) => ({
      ...prev,
      email: displayEmail ? (prev.email || displayEmail) : prev.email,
      phone: hasPhone ? (prev.phone || user.phone || '') : prev.phone,
      phoneCode: prev.phoneCode || user.phoneCode || '+91'
    }));
  }, [user?.id, user?.email, user?.phone]);

  const saveDraft = useCallback(() => {
    localStorage.setItem(DRAFT_KEY, JSON.stringify({
      step, personal, addresses, vehicles
    }));
  }, [step, personal, addresses, vehicles]);

  useEffect(() => {
    const t = setInterval(saveDraft, 45000);
    return () => clearInterval(t);
  }, [saveDraft]);

  const setAddress = (i, field, value) => {
    setAddresses((a) => {
      const next = [...a];
      next[i] = { ...next[i], [field]: value };
      if (field === 'isDefault' && value) {
        next.forEach((addr, j) => { if (j !== i) addr.isDefault = false; });
      }
      return next;
    });
  };

  const setVehicle = (i, field, value) => {
    setVehicles((v) => {
      const next = [...v];
      next[i] = { ...next[i], [field]: value };
      return next;
    });
  };

  const handleSubmit = async () => {
    setError('');
    if (!personal.firstName?.trim()) {
      setError('First name is required');
      return;
    }
    const addr = addresses[0];
    if (!addr?.area?.trim() || !addr?.city?.trim() || !addr?.state?.trim() || !addr?.pincode?.trim()) {
      setError('At least one full address (Area, City, State, Pincode) is required');
      return;
    }
    setLoading(true);
    try {
      const updatedUser = await authService.completeOnboarding({
        role: USER_ROLES.CUSTOMER,
        customerDetails: { personal, addresses, vehicles }
      });
      if (updatedUser && updateUser) updateUser(updatedUser);
      localStorage.removeItem(DRAFT_KEY);
      localStorage.setItem('userRole', USER_ROLES.CUSTOMER);
      navigate(getDashboardPath(USER_ROLES.CUSTOMER));
    } catch (err) {
      setError(err.message || 'Failed to submit');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Wrap className="onboarding-light-page">
      <Card>
        <OnboardingStepper steps={STEPS} currentStep={step} />
        <Title>Customer onboarding</Title>
        <Sub>Step {step} of {STEPS.length}: {STEPS[step - 1]}</Sub>
        {error && <ErrorP>{error}</ErrorP>}
        <StepContent>
        {step === 1 && (
          <Section>
            <Row3>
              <Field><Label>First Name *</Label><Input value={personal.firstName} onChange={(e) => setPersonal((p) => ({ ...p, firstName: e.target.value }))} placeholder="First name" /></Field>
              <Field><Label>Last Name</Label><Input value={personal.lastName} onChange={(e) => setPersonal((p) => ({ ...p, lastName: e.target.value }))} placeholder="Last name" /></Field>
              <Field><Label>Gender</Label><Select value={personal.gender} onChange={(e) => setPersonal((p) => ({ ...p, gender: e.target.value }))}><option value="">Select</option><option value="male">Male</option><option value="female">Female</option><option value="other">Other</option></Select></Field>
            </Row3>
            <Row3>
              <Field><Label>Email *</Label><Input type="email" value={personal.email} onChange={(e) => setPersonal((p) => ({ ...p, email: e.target.value }))} placeholder="you@example.com" /></Field>
              <Field><Label>Mobile Number *</Label><div style={{ display: 'flex', gap: 8 }}><Select value={personal.phoneCode} onChange={(e) => setPersonal((p) => ({ ...p, phoneCode: e.target.value }))} style={{ width: 90 }}><option value="+91">+91</option><option value="+1">+1</option></Select><Input value={personal.phone} onChange={(e) => setPersonal((p) => ({ ...p, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))} placeholder="10-digit mobile" /></div></Field>
              <Field><Label>DOB</Label><Input type="date" value={personal.dob} onChange={(e) => setPersonal((p) => ({ ...p, dob: e.target.value }))} /></Field>
            </Row3>
            <Row3>
              <Field><FileUploadField label="Profile Photo" value={personal.profilePhoto} onChange={(v) => setPersonal((p) => ({ ...p, profilePhoto: v }))} accept="image/*" /></Field>
              <Field><Label>Preferred Language</Label><Select value={personal.preferredLanguage} onChange={(e) => setPersonal((p) => ({ ...p, preferredLanguage: e.target.value }))}><option value="en">English</option><option value="hi">Hindi</option></Select></Field>
              <Field></Field>
            </Row3>
          </Section>
        )}

        {step === 2 && (
          <>
            <Section>
              <Row2><Field style={{ gridColumn: '1 / -1' }}><Label>Area / Locality *</Label><Input value={addresses[0]?.area} onChange={(e) => setAddress(0, 'area', e.target.value)} placeholder="Area / locality" /></Field></Row2>
              <Row2><Field><Label>Address Line 2</Label><Input value={addresses[0]?.addressLine2} onChange={(e) => setAddress(0, 'addressLine2', e.target.value)} placeholder="Address line 2" /></Field><Field><Label>Landmark</Label><Input value={addresses[0]?.landmark} onChange={(e) => setAddress(0, 'landmark', e.target.value)} /></Field></Row2>
              <Row3>
                <Field><Label>City *</Label><Input value={addresses[0]?.city} onChange={(e) => setAddress(0, 'city', e.target.value)} /></Field>
                <Field><Label>State *</Label><Input value={addresses[0]?.state} onChange={(e) => setAddress(0, 'state', e.target.value)} /></Field>
                <Field><Label>Pincode *</Label><Input value={addresses[0]?.pincode} onChange={(e) => setAddress(0, 'pincode', e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="6 digits" /></Field>
              </Row3>
              <Row3><Field><Label>Country</Label><Input value={addresses[0]?.country} onChange={(e) => setAddress(0, 'country', e.target.value)} /></Field><Field><Label>Lat</Label><Input value={addresses[0]?.lat} onChange={(e) => setAddress(0, 'lat', e.target.value)} placeholder="Latitude" /></Field><Field><Label>Lng</Label><Input value={addresses[0]?.lng} onChange={(e) => setAddress(0, 'lng', e.target.value)} placeholder="Longitude" /></Field></Row3>
              <Row2>
                <Field><Label>Label</Label><Select value={addresses[0]?.label} onChange={(e) => setAddress(0, 'label', e.target.value)}><option value="Home">Home</option><option value="Work">Work</option><option value="Other">Other</option></Select></Field>
                <Field><Label style={{ display: 'flex', alignItems: 'center', gap: 6 }}><input type="checkbox" checked={!!addresses[0]?.isDefault} onChange={(e) => setAddress(0, 'isDefault', e.target.checked)} /> Default address</Label></Field>
              </Row2>
            </Section>
          </>
        )}

        {step === 3 && (
          <>
            <Section>
              <Row3><Field><Label>Vehicle Type</Label><Input value={vehicles[0]?.vehicleType} onChange={(e) => setVehicle(0, 'vehicleType', e.target.value)} placeholder="Car/Bike" /></Field><Field><Label>Brand</Label><Input value={vehicles[0]?.brand} onChange={(e) => setVehicle(0, 'brand', e.target.value)} /></Field><Field><Label>Model</Label><Input value={vehicles[0]?.model} onChange={(e) => setVehicle(0, 'model', e.target.value)} /></Field></Row3>
              <Row3><Field><Label>Variant</Label><Input value={vehicles[0]?.variant} onChange={(e) => setVehicle(0, 'variant', e.target.value)} /></Field><Field><Label>Fuel Type</Label><Input value={vehicles[0]?.fuelType} onChange={(e) => setVehicle(0, 'fuelType', e.target.value)} /></Field><Field><Label>Year</Label><Input value={vehicles[0]?.year} onChange={(e) => setVehicle(0, 'year', e.target.value)} /></Field></Row3>
              <Row3><Field><Label>Registration No</Label><Input value={vehicles[0]?.registrationNumber} onChange={(e) => setVehicle(0, 'registrationNumber', e.target.value)} /></Field><Field><Label>Odometer</Label><Input value={vehicles[0]?.odometer} onChange={(e) => setVehicle(0, 'odometer', e.target.value)} /></Field><Field><FileUploadField label="RC Document" value={vehicles[0]?.rcUpload} onChange={(v) => setVehicle(0, 'rcUpload', v)} accept="image/*,.pdf" /></Field>              </Row3>
            </Section>
          </>
        )}
        </StepContent>

        <BtnRow>
          <Btn type="button" className="secondary" onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1}>Back</Btn>
          {step < STEPS.length ? (
            <Btn type="button" className="primary" onClick={() => { saveDraft(); setStep((s) => s + 1); }}>Next</Btn>
          ) : (
            <Btn type="button" className="primary" onClick={handleSubmit} disabled={loading}>{loading ? 'Submitting...' : 'Submit'}</Btn>
          )}
        </BtnRow>
      </Card>
    </Wrap>
  );
}
